import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import apiClient from '../api/client';
import { useTheme } from '../contexts/ThemeContext';
import { Plus, Upload, Download, DollarSign, TrendingUp, Percent, Edit, Trash2, Save, X } from 'lucide-react';

const Incentives = () => {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('schemes');
  const [schemes, setSchemes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSchemeModal, setShowAddSchemeModal] = useState(false);
  const [showEditSchemeModal, setShowEditSchemeModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [uploadStage, setUploadStage] = useState('');
  const [uploadPercent, setUploadPercent] = useState(0);
  const [editingScheme, setEditingScheme] = useState(null);
  const [newScheme, setNewScheme] = useState({
    name: '',
    type: 'kpi_based',
    description: '',
    assigned_to: [],
    slabs: [
      { min: 0, max: 60, percentage: 0, amount: 0 },
      { min: 61, max: 80, percentage: 5, amount: 2500 },
      { min: 81, max: 100, percentage: 10, amount: 5000 }
    ]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schemesRes, empRes] = await Promise.all([
        apiClient.get('/api/incentives/schemes'),
        apiClient.get('/api/employees')
      ]);
      setSchemes(schemesRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddScheme = async (e) => {
    e.preventDefault();
    try {
      const schemeData = {
        name: newScheme.name,
        type: newScheme.type,
        description: newScheme.description,
        applicable_to: newScheme.assigned_to,
        slabs: newScheme.slabs.map(slab => ({
          min_score: slab.min,
          max_score: slab.max,
          rate: slab.percentage / 100,
          fixed_amount: slab.amount
        }))
      };
      
      await apiClient.post('/api/incentives/schemes', schemeData);
      fetchData();
      setShowAddSchemeModal(false);
      setNewScheme({
        name: '',
        type: 'kpi_based',
        description: '',
        assigned_to: [],
        slabs: [
          { min: 0, max: 60, percentage: 0, amount: 0 },
          { min: 61, max: 80, percentage: 5, amount: 2500 },
          { min: 81, max: 100, percentage: 10, amount: 5000 }
        ]
      });
    } catch (error) {
      console.error('Error adding scheme:', error);
      alert('Error adding incentive scheme');
    }
  };

  const handleEditScheme = (scheme) => {
    const editData = {
      id: scheme.id,
      name: scheme.name,
      type: scheme.type,
      description: scheme.description,
      assigned_to: scheme.applicable_to || [],
      slabs: (scheme.slabs || []).map(slab => ({
        min: slab.min_score,
        max: slab.max_score,
        percentage: (slab.rate * 100),
        amount: slab.fixed_amount || 0
      }))
    };
    setEditingScheme(editData);
    setShowEditSchemeModal(true);
  };

  const handleUpdateScheme = async (e) => {
    e.preventDefault();
    try {
      const schemeData = {
        name: editingScheme.name,
        type: editingScheme.type,
        description: editingScheme.description,
        applicable_to: editingScheme.assigned_to,
        slabs: editingScheme.slabs.map(slab => ({
          min_score: slab.min,
          max_score: slab.max,
          rate: slab.percentage / 100,
          fixed_amount: slab.amount
        }))
      };
      
      await apiClient.put(`/api/incentives/schemes/${editingScheme.id}`, schemeData);
      fetchData();
      setShowEditSchemeModal(false);
      setEditingScheme(null);
    } catch (error) {
      console.error('Error updating scheme:', error);
      alert('Error updating scheme');
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? `${emp.emp_code} - ${emp.full_name}` : empId;
  };

  const handleDeleteScheme = async (schemeId, schemeName) => {
    if (!window.confirm(`Are you sure you want to delete "${schemeName}"?`)) {
      return;
    }
    
    try {
      await apiClient.delete(`/api/incentives/schemes/${schemeId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting scheme:', error);
      alert('Error deleting scheme');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        alert('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setUploadProgress(true);
    setUploadStage('Uploading file...');
    setUploadPercent(10);
    
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      setUploadPercent(40);
      setUploadStage('Processing incentive scheme...');
      
      await apiClient.post('/api/incentives/schemes/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadPercent(90);
      setUploadStage('Finalizing...');
      await new Promise(resolve => setTimeout(resolve, 500));
      setUploadPercent(100);
      
      alert('Incentive scheme uploaded successfully!');
      fetchData();
      setShowUploadModal(false);
      setSelectedFile(null);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setUploadProgress(false);
      setUploadPercent(0);
      setUploadStage('');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await apiClient.get('/api/incentives/schemes/template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'incentive_scheme_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
    }
  };

  const addSlab = (isEditing = false) => {
    const targetScheme = isEditing ? editingScheme : newScheme;
    const setScheme = isEditing ? setEditingScheme : setNewScheme;
    
    setScheme({
      ...targetScheme,
      slabs: [...targetScheme.slabs, { min: 0, max: 0, percentage: 0, amount: 0 }]
    });
  };

  const updateSlab = (index, field, value, isEditing = false) => {
    const targetScheme = isEditing ? editingScheme : newScheme;
    const setScheme = isEditing ? setEditingScheme : setNewScheme;
    
    const updatedSlabs = [...targetScheme.slabs];
    updatedSlabs[index][field] = parseFloat(value) || 0;
    setScheme({ ...targetScheme, slabs: updatedSlabs });
  };

  const removeSlab = (index, isEditing = false) => {
    const targetScheme = isEditing ? editingScheme : newScheme;
    const setScheme = isEditing ? setEditingScheme : setNewScheme;
    
    const updatedSlabs = targetScheme.slabs.filter((_, i) => i !== index);
    setScheme({ ...targetScheme, slabs: updatedSlabs });
  };

  if (loading) {
    return (
      <Layout>
        <div className='flex items-center justify-center h-full'>
          <div className='loading-spinner'></div>
        </div>
      </Layout>
    );
  }

  const renderSlabForm = (scheme, setScheme, isEditing = false) => (
    <div>
      <div className='flex items-center justify-between mb-3'>
        <label className='block text-sm font-medium text-[#262626]'>Incentive Slabs*</label>
        <button
          type='button'
          onClick={() => addSlab(isEditing)}
          className='text-sm text-[#7EC845] hover:text-[#6EB835] font-medium'
        >
          + Add Slab
        </button>
      </div>
      <div className='space-y-3'>
        <div className='grid grid-cols-5 gap-2 text-xs font-medium text-[#737373] mb-2'>
          <div>Min</div>
          <div>Max</div>
          <div>%</div>
          <div>Amount (₹)</div>
          <div></div>
        </div>
        {scheme.slabs.map((slab, index) => (
          <div key={index} className='grid grid-cols-5 gap-2 items-center'>
            <input
              type='number'
              required
              value={slab.min}
              onChange={(e) => updateSlab(index, 'min', e.target.value, isEditing)}
              className='w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm'
              placeholder='0'
            />
            <input
              type='number'
              required
              value={slab.max}
              onChange={(e) => updateSlab(index, 'max', e.target.value, isEditing)}
              className='w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm'
              placeholder='100'
            />
            <input
              type='number'
              step='0.1'
              value={slab.percentage}
              onChange={(e) => updateSlab(index, 'percentage', e.target.value, isEditing)}
              className='w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm'
              placeholder='5'
            />
            <input
              type='number'
              value={slab.amount}
              onChange={(e) => updateSlab(index, 'amount', e.target.value, isEditing)}
              className='w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm'
              placeholder='5000'
            />
            <div className='flex gap-1'>
              {scheme.slabs.length > 1 && (
                <button
                  type='button'
                  onClick={() => removeSlab(index, isEditing)}
                  className='px-2 py-2 text-red-600 hover:bg-red-50 rounded-lg'
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className='text-xs text-[#737373] mt-2'>
        % = Percentage to calculate, Amount = Fixed amount. Total incentive = (Base Salary × % / 100) + Amount
      </p>
    </div>
  );

  return (
    <Layout>
      <div className='page-header flex items-center justify-between'>
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Incentive Management</h1>
          <p className={`mt-1 ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-600'}`}>Manage incentive schemes and payouts</p>
        </div>
        <div className='flex gap-3'>
          <button
            onClick={() => setShowUploadModal(true)}
            data-testid='upload-incentive-button'
            className='flex items-center gap-2 bg-[#10B981] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#059669] transition-all'
          >
            <Upload size={20} />
            Upload Excel
          </button>
          <button
            onClick={() => setShowAddSchemeModal(true)}
            data-testid='add-scheme-button'
            className='flex items-center gap-2 bg-[#00B8B8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0ED2D2] transition-all'
          >
            <Plus size={20} />
            Add Scheme
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className='border-b border-[#E5E5E5] mb-6'>
        <div className='flex gap-6'>
          <button
            onClick={() => setActiveTab('schemes')}
            className={`pb-3 px-2 font-medium transition-colors ${activeTab === 'schemes' ? 'text-[#7EC845] border-b-2 border-[#7EC845]' : 'text-[#737373] hover:text-[#262626]'}`}
          >
            Incentive Schemes
          </button>
          <button
            onClick={() => setActiveTab('payouts')}
            className={`pb-3 px-2 font-medium transition-colors ${activeTab === 'payouts' ? 'text-[#7EC845] border-b-2 border-[#7EC845]' : 'text-[#737373] hover:text-[#262626]'}`}
          >
            Payouts
          </button>
        </div>
      </div>

      {/* Schemes Tab */}
      {activeTab === 'schemes' && (
        <div>
          {/* Summary Cards */}
          <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-6'>
            <div className='bg-white rounded-xl border border-[#E5E5E5] p-6'>
              <div className='flex items-center justify-between mb-2'>
                <DollarSign className='text-[#7EC845]' size={24} />
                <span className='text-2xl font-bold text-[#262626]'>{schemes.length}</span>
              </div>
              <p className='text-sm text-[#737373]'>Active Schemes</p>
            </div>
            <div className='bg-white rounded-xl border border-[#E5E5E5] p-6'>
              <div className='flex items-center justify-between mb-2'>
                <TrendingUp className='text-[#10B981]' size={24} />
                <span className='text-2xl font-bold text-[#262626]'>
                  {schemes.filter(s => s.type === 'kpi_based').length}
                </span>
              </div>
              <p className='text-sm text-[#737373]'>KPI-Based</p>
            </div>
            <div className='bg-white rounded-xl border border-[#E5E5E5] p-6'>
              <div className='flex items-center justify-between mb-2'>
                <Percent className='text-[#1B5E4A]' size={24} />
                <span className='text-2xl font-bold text-[#262626]'>
                  {schemes.filter(s => s.type === 'commission').length}
                </span>
              </div>
              <p className='text-sm text-[#737373]'>Commission-Based</p>
            </div>
          </div>

          {/* Schemes List */}
          <div className='bg-white rounded-xl border border-[#E5E5E5]'>
            <div className='p-6 border-b border-[#E5E5E5]'>
              <h3 className='text-lg font-semibold text-[#262626]'>Incentive Schemes</h3>
            </div>
            <div className='p-6 space-y-4'>
              {schemes.length === 0 ? (
                <div className='text-center py-12 text-[#737373]'>
                  <DollarSign size={48} className='mx-auto mb-4 opacity-50' />
                  <p>No incentive schemes configured. Add a scheme to get started.</p>
                </div>
              ) : (
                schemes.map((scheme) => (
                  <div key={scheme.id} className='border border-[#E5E5E5] rounded-lg p-6 hover:shadow-md transition-shadow'>
                    <div className='flex items-start justify-between mb-4'>
                      <div className='flex-1'>
                        <h4 className='font-semibold text-[#262626] text-lg mb-2'>{scheme.name}</h4>
                        <p className='text-sm text-[#737373] mb-3'>{scheme.description}</p>
                        <span className='inline-block px-3 py-1 bg-[#7EC845]/10 text-[#7EC845] rounded-full text-xs font-medium'>
                          {scheme.type.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className='flex gap-2'>
                        <button
                          onClick={() => handleEditScheme(scheme)}
                          className='p-2 text-[#1B5E4A] hover:bg-[#1B5E4A]/10 rounded-lg'
                          title='Edit Scheme'
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteScheme(scheme.id, scheme.name)}
                          className='p-2 text-red-600 hover:bg-red-50 rounded-lg'
                          title='Delete Scheme'
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    {/* Slabs Table */}
                    {scheme.slabs && scheme.slabs.length > 0 && (
                      <div className='mt-4'>
                        <h5 className='text-sm font-semibold text-[#262626] mb-3'>Incentive Slabs:</h5>
                        <div className='overflow-x-auto'>
                          <table className='w-full text-sm'>
                            <thead className='bg-[#F5F5F5]'>
                              <tr>
                                <th className='text-left p-3 font-medium text-[#262626]'>Min</th>
                                <th className='text-left p-3 font-medium text-[#262626]'>Max</th>
                                <th className='text-left p-3 font-medium text-[#262626]'>%</th>
                                <th className='text-left p-3 font-medium text-[#262626]'>Amount</th>
                                <th className='text-left p-3 font-medium text-[#262626]'>Assigned To</th>
                              </tr>
                            </thead>
                            <tbody>
                              {scheme.slabs.map((slab, idx) => {
                                const fixedAmount = slab.fixed_amount || 0;
                                
                                return (
                                  <tr key={`${slab.min_score}-${slab.max_score}-${idx}`} className='border-t border-[#E5E5E5]'>
                                    <td className='p-3 text-[#737373]'>{slab.min_score}</td>
                                    <td className='p-3 text-[#737373]'>{slab.max_score}</td>
                                    <td className='p-3 font-medium text-[#262626]'>{((slab.rate || 0) * 100).toFixed(1)}%</td>
                                    <td className='p-3 text-[#10B981] font-mono'>₹{fixedAmount.toLocaleString()}</td>
                                    <td className='p-3'>
                                      {idx === 0 && scheme.applicable_to && scheme.applicable_to.length > 0 ? (
                                        <div className='flex flex-wrap gap-1'>
                                          {scheme.applicable_to.slice(0, 3).map((empId, i) => (
                                            <span key={empId || i} className='px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs'>
                                              {getEmployeeName(empId).split(' - ')[0]}
                                            </span>
                                          ))}
                                          {scheme.applicable_to.length > 3 && (
                                            <span className='px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs'>
                                              +{scheme.applicable_to.length - 3}
                                            </span>
                                          )}
                                        </div>
                                      ) : idx === 0 ? (
                                        <span className='text-xs text-[#737373]'>All Employees</span>
                                      ) : null}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payouts Tab */}
      {activeTab === 'payouts' && (
        <div className='bg-white rounded-xl border border-[#E5E5E5] p-6'>
          <h3 className='text-lg font-semibold text-[#262626] mb-4'>Incentive Payouts</h3>
          <div className='text-center py-12 text-[#737373]'>
            <DollarSign size={48} className='mx-auto mb-4 opacity-50' />
            <p>Payout tracking will be available here. Incentives are automatically calculated during payroll processing.</p>
          </div>
        </div>
      )}

      {/* Add Scheme Modal */}
      {showAddSchemeModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50' onClick={() => setShowAddSchemeModal(false)}>
          <div className='bg-white rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[#262626] mb-6'>Add Incentive Scheme</h2>
            <form onSubmit={handleAddScheme} className='space-y-6'>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-[#262626] mb-2'>Scheme Name*</label>
                  <input
                    type='text'
                    required
                    value={newScheme.name}
                    onChange={(e) => setNewScheme({...newScheme, name: e.target.value})}
                    className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                    placeholder='e.g., Q1 2025 Performance Bonus'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[#262626] mb-2'>Type*</label>
                  <select
                    required
                    value={newScheme.type}
                    onChange={(e) => setNewScheme({...newScheme, type: e.target.value})}
                    className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                  >
                    <option value='kpi_based'>KPI Based</option>
                    <option value='commission'>Commission</option>
                    <option value='fixed'>Fixed</option>
                  </select>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Description</label>
                <textarea
                  value={newScheme.description}
                  onChange={(e) => setNewScheme({...newScheme, description: e.target.value})}
                  rows='2'
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                  placeholder='Brief description of the incentive scheme'
                ></textarea>
              </div>

              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Assign to Employees</label>
                <select
                  multiple
                  value={newScheme.assigned_to}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setNewScheme({...newScheme, assigned_to: selected});
                  }}
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845] min-h-[120px]'
                >
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.emp_code} - {emp.full_name}
                    </option>
                  ))}
                </select>
                <p className='text-xs text-[#737373] mt-1'>Hold Ctrl/Cmd to select multiple employees. Leave empty to apply to all.</p>
              </div>

              {renderSlabForm(newScheme, setNewScheme, false)}

              <div className='flex gap-4 justify-end mt-6 pt-4 border-t'>
                <button
                  type='button'
                  onClick={() => setShowAddSchemeModal(false)}
                  className='px-6 py-2 border border-[#E5E5E5] rounded-lg text-[#262626] hover:bg-[#F5F5F5]'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='px-6 py-2 bg-[#7EC845] text-white rounded-lg hover:bg-[#6EB835]'
                >
                  Add Scheme
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Scheme Modal */}
      {showEditSchemeModal && editingScheme && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50' onClick={() => setShowEditSchemeModal(false)}>
          <div className='bg-white rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[#262626] mb-6'>Edit Incentive Scheme</h2>
            <form onSubmit={handleUpdateScheme} className='space-y-6'>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-[#262626] mb-2'>Scheme Name*</label>
                  <input
                    type='text'
                    required
                    value={editingScheme.name}
                    onChange={(e) => setEditingScheme({...editingScheme, name: e.target.value})}
                    className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                  />
                </div>
                <div>
                  <label className='block text-sm font-medium text-[#262626] mb-2'>Type*</label>
                  <select
                    required
                    value={editingScheme.type}
                    onChange={(e) => setEditingScheme({...editingScheme, type: e.target.value})}
                    className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                  >
                    <option value='kpi_based'>KPI Based</option>
                    <option value='commission'>Commission</option>
                    <option value='fixed'>Fixed</option>
                  </select>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Description</label>
                <textarea
                  value={editingScheme.description}
                  onChange={(e) => setEditingScheme({...editingScheme, description: e.target.value})}
                  rows='2'
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]'
                ></textarea>
              </div>

              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Assign to Employees</label>
                <select
                  multiple
                  value={editingScheme.assigned_to}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setEditingScheme({...editingScheme, assigned_to: selected});
                  }}
                  className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845] min-h-[120px]'
                >
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.emp_code} - {emp.full_name}
                    </option>
                  ))}
                </select>
                <p className='text-xs text-[#737373] mt-1'>Hold Ctrl/Cmd to select multiple employees. Leave empty to apply to all.</p>
              </div>

              {renderSlabForm(editingScheme, setEditingScheme, true)}

              <div className='flex gap-4 justify-end mt-6 pt-4 border-t'>
                <button
                  type='button'
                  onClick={() => {
                    setShowEditSchemeModal(false);
                    setEditingScheme(null);
                  }}
                  className='px-6 py-2 border border-[#E5E5E5] rounded-lg text-[#262626] hover:bg-[#F5F5F5]'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  className='flex items-center gap-2 px-6 py-2 bg-[#7EC845] text-white rounded-lg hover:bg-[#6EB835]'
                >
                  <Save size={18} />
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50' onClick={() => setShowUploadModal(false)}>
          <div className='bg-white rounded-xl p-6 max-w-md w-full m-4' onClick={(e) => e.stopPropagation()}>
            <h2 className='text-2xl font-bold text-[#262626] mb-6'>Bulk Upload Incentive Scheme</h2>
            <div className='space-y-4'>
              <div>
                <p className='text-sm text-[#737373] mb-4'>Upload an Excel file with incentive scheme details.</p>
                <button onClick={handleDownloadTemplate} className='flex items-center gap-2 px-4 py-2 border border-[#7EC845] text-[#7EC845] rounded-lg hover:bg-[#7EC845]/10 mb-4'>
                  <Download size={18} />Download Template
                </button>
              </div>
              <div>
                <label className='block text-sm font-medium text-[#262626] mb-2'>Select Excel File</label>
                <input type='file' accept='.xlsx,.xls' onChange={handleFileSelect} className='w-full px-4 py-2 border border-[#E5E5E5] rounded-lg' />
                {selectedFile && (<p className='text-sm text-[#737373] mt-2'>Selected: {selectedFile.name}</p>)}
              </div>
              <div className='flex gap-4 justify-end mt-6'>
                <button type='button' onClick={() => { setShowUploadModal(false); setSelectedFile(null); }} disabled={uploadProgress} className='px-6 py-2 border border-[#E5E5E5] rounded-lg text-[#262626] hover:bg-[#F5F5F5] disabled:opacity-50'>Cancel</button>
                <button onClick={handleBulkUpload} disabled={!selectedFile || uploadProgress} className='px-6 py-2 bg-[#7EC845] text-white rounded-lg hover:bg-[#6EB835] disabled:opacity-50'>{uploadProgress ? 'Uploading...' : 'Upload'}</button>
              </div>
              
              {uploadProgress && (
                <div className='mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                  <div className='flex items-center justify-between mb-2'>
                    <span className='text-sm font-medium text-blue-900'>{uploadStage}</span>
                    <span className='text-sm font-bold text-blue-900'>{uploadPercent}%</span>
                  </div>
                  <div className='w-full bg-blue-200 rounded-full h-3'>
                    <div className='bg-[#7EC845] h-3 rounded-full transition-all duration-500' style={{ width: `${uploadPercent}%` }}></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Incentives;